import { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import {
  getImages,
  editImages,
  deleteLogoImage,
  deleteBackgroundImage,
} from "../../../../../api/pub-grade";
import { pubState } from "../../../../../recoil/pub.atom";

export const useTimerImage = () => {
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [backgroundImage, setBackgroundImage] = useState<File | null>(null);
  const [coverImageUrl, setCoverImageUrl] = useState<string | null>(null);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState<string | null>(
    null,
  );
  const [isLoad, setIsLoad] = useState(false);
  const pub = useRecoilValue(pubState);

  // 로고이미지, 배경이미지 조회
  const getCoverImage = async () => {
    if (!pub) return;
    try {
      const res = await getImages(pub.id);
      setCoverImageUrl(res.coverImageUrl);
      setBackgroundImageUrl(res.backgroundImageUrl);
    } catch (e) {}
  };

  // 파일 업로드 시 자동 업데이트
  useEffect(() => {
    if (isLoad) {
      // 초기 진입 시 업데이트 안 함
      updateImage();
    } else {
      getCoverImage();
      setIsLoad(true);
    }
  }, [coverImage, backgroundImage]);

  // 로고이미지, 배경이미지 업데이트
  const updateImage = async () => {
    if (!pub) {
      return false;
    }

    try {
      const formData = new FormData();
      if (coverImage) {
        formData.append("coverImageFile", coverImage);
      }
      if (backgroundImage) {
        formData.append("backgroundImageFile", backgroundImage);
      }

      await editImages(pub.id, formData);
      getCoverImage();
    } catch (error) {
      console.error(error);
    }
  };

  // 이미지 사이즈 유효성
  const validateImageDimensions = (
    width: number,
    height: number,
    isCover: boolean,
  ): string | null => {
    const minWidth = isCover ? 50 : 400;
    const minHeight = isCover ? 50 : 400;
    const maxWidth = isCover ? 260 : 1600;
    const maxHeight = isCover ? 260 : 1600;

    if (width < minWidth || height < minHeight) {
      return `이미지 크기가 너무 작습니다. (최소 ${minWidth}x${minHeight}px)`;
    }
    if (width > maxWidth || height > maxHeight) {
      return `이미지 크기가 너무 큽니다. (최대 ${maxWidth}x${maxHeight}px)`;
    }
    return null;
  };

  // 이미지 유효성
  const validateFile = async (
    file: File,
    isCover: boolean,
  ): Promise<string | null> => {
    if (file.size > 10 * 1024 * 1024) {
      return `파일 크기는 ${10}MB 이하여야 합니다.`;
    }

    return new Promise((resolve) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(img.src);
        resolve(validateImageDimensions(img.width, img.height, isCover));
      };
      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        resolve("이미지 크기를 확인할 수 없습니다.");
      };
    });
  };

  const handleClick = (isCover: boolean = true) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/jpeg, image/png, image/gif, image/webp";
    input.onchange = async (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const error = await validateFile(file, isCover);
        if (error) {
          alert(error);
          return;
        }

        if (isCover) setCoverImage(file);
        if (!isCover) setBackgroundImage(file);
      }
    };
    input.click();
  };

  const handleDrop = (
    e: React.DragEvent<HTMLDivElement>,
    isCover: boolean = true,
  ) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      if (isCover) setCoverImage(file);
      if (!isCover) setBackgroundImage(file);
    }
  };

  const handleDeleteBackground = async () => {
    if (!pub) return;
    try {
      await deleteBackgroundImage(pub.id);
      getCoverImage();
      setBackgroundImage(null);
    } catch (e) {
      console.error(e);
      return;
    }
  };

  const handleDeleteCover = async () => {
    if (!pub) return;
    try {
      await deleteLogoImage(pub.id);
      getCoverImage();
      setCoverImage(null);
    } catch (e) {
      console.error(e);
      return;
    }
  };

  return {
    // 이미지 url
    coverImageUrl,
    backgroundImageUrl,
    // 이미지 업로드
    handleClick,
    handleDrop,
    // 이미지 삭제
    handleDeleteBackground,
    handleDeleteCover,
  };
};
